import React, { useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import Tooltip from '@atlaskit/tooltip';

import { AudioFeatureMenuGroup, type AudioFeatureMenuItem } from './AudioFeatureMenuGroup';

const mergeRefs = <T,>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> => {
	return (node: T) => {
		refs.forEach((ref) => {
			if (!ref) {
				return;
			}
			if (typeof ref === 'function') {
				ref(node);
			} else {
				(ref as React.MutableRefObject<T | null>).current = node;
			}
		});
	};
};

const messages = defineMessages({
	moreOptionsLabel: {
		id: 'ai-audio.audio-player.overflow-menu.more-options-label',
		defaultMessage: 'More options',
		description: 'Label for the more options button in the overflow menu',
	},
});

type OverflowMenuProps = {
	items: AudioFeatureMenuItem[];
	buttonRef?: React.Ref<HTMLButtonElement>;
};

export const OverflowMenu = ({ items, buttonRef }: OverflowMenuProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();
	return (
		<Tooltip
			position="bottom"
			canAppear={() => !isOpen}
			content={formatMessage(messages.moreOptionsLabel)}
		>
			<DropdownMenu
				onOpenChange={({ isOpen: newIsOpen }) => setIsOpen(newIsOpen)}
				trigger={({ triggerRef, ...triggerProps }) => (
					<IconButton
						{...triggerProps}
						ref={mergeRefs(triggerRef, buttonRef)}
						appearance="subtle"
						spacing="compact"
						icon={ShowMoreHorizontalIcon}
						label={formatMessage(messages.moreOptionsLabel)}
					/>
				)}
				shouldRenderToParent={true}
			>
				<AudioFeatureMenuGroup items={items} />
			</DropdownMenu>
		</Tooltip>
	);
};
